import * as Phaser from 'phaser';

export const VIDEO_TRANSITION_SCENE_KEY = 'VideoTransitionScene' as const;

export interface VideoTransitionConfig {
  videoUrl: string;
  sourceSceneKey: string;
  targetSceneKey: string;
  durationMs?: number;
  fadeInMs?: number;
  fadeOutMs?: number;
}

export class VideoTransitionScene extends Phaser.Scene {
  private config!: VideoTransitionConfig;
  private video: Phaser.GameObjects.Video | null = null;
  private transitioned = false;

  constructor() {
    super(VIDEO_TRANSITION_SCENE_KEY);
  }

  init(data: VideoTransitionConfig): void {
    this.transitioned = false;
    this.config = {
      durationMs: 5000,
      fadeInMs: 300,
      fadeOutMs: 600,
      ...data,
    };
  }

  preload(): void {
    const key = `transition-video-${Date.now()}`;
    this.load.video(key, this.config.videoUrl, true);
    this.registry.set('__videoTransitionKey', key);
  }

  create(): void {
    const { width, height } = this.scale;
    const key = this.registry.get('__videoTransitionKey') as string;

    this.cameras.main.setBackgroundColor(0x000000);

    this.video = this.add.video(width / 2, height / 2, key);
    this.video.setDisplaySize(width, height);
    this.video.setAlpha(0);

    this.tweens.add({
      targets: this.video,
      alpha: 1,
      duration: this.config.fadeInMs,
      ease: 'Cubic.easeOut',
      onComplete: () => {
        this.video?.play(false);
      },
    });

    if (this.video) {
      this.video.on('complete', () => {
        this.transitionToTarget();
      });
    }

    const maxDuration = (this.config.durationMs ?? 5000) + (this.config.fadeInMs ?? 300) + 500;
    this.time.delayedCall(maxDuration, () => {
      this.transitionToTarget();
    });
  }

  private transitionToTarget(): void {
    if (this.transitioned || !this.video) return;
    this.transitioned = true;

    this.tweens.add({
      targets: this.video,
      alpha: 0,
      duration: this.config.fadeOutMs,
      ease: 'Cubic.easeIn',
      onComplete: () => {
        this.scene.start(this.config.targetSceneKey);
        this.scene.stop(this.config.sourceSceneKey);
        this.scene.stop();
      },
    });
  }

  shutdown(): void {
    this.video?.destroy();
    this.video = null;
  }
}
